import { storeMoneyMinor } from "@libs/platform-wire";
import type {
  ChosenDelivery,
  DeliveryEstimate,
  DeliveryOption,
} from "./contracts";

// A merch order's delivery options, one flow for every merch purchase (the
// sheet's Delivery step, the checkout's delivery line, the start's pinned
// option), as the purchase describes them (`createPurchaseDelivery`).

/** A basket: each line's item and quantity. */
export type DeliveryLines = readonly { item_id: string; quantity: number }[];

/** The delivery for one basket, as the sheet shows it. `loading` while the
 *  store is being asked; `ready` with every option it offers (cheapest
 *  first) and the estimate a start pins one of (none for a listing's own
 *  fee); `short` with how many of each line the store has; `undeliverable`
 *  when it posts none of the basket; `failed` with the store's own words
 *  where it gave any, to ask again. `idle` with no basket. */
export type DeliveryQuote =
  | { status: "idle"; key: null }
  | { status: "loading"; key: string }
  | { status: "ready"; key: string; estimateId: string | null; options: DeliveryOption[] }
  | { status: "short"; key: string; stock: DeliveryEstimate["stock"] }
  | { status: "undeliverable"; key: string }
  | { status: "failed"; key: string; message: string | null };

/** The one option of a listing's own delivery fee: "UK delivery". */
export const LISTING_DELIVERY_CODE = "listing";

/** The key of a basket: its lines, in item order, without the empty ones. */
export const deliveryKeyOf = (lines: DeliveryLines): string =>
  JSON.stringify(
    [...lines]
      .filter((line) => line.quantity > 0)
      .map(({ item_id, quantity }) => ({ item_id, quantity }))
      .sort((a, b) => a.item_id.localeCompare(b.item_id)),
  );

/** How long the sheet waits between reads of the store's answer. */
export const DELIVERY_POLL_MS = 500;
/** How long it waits for an answer at all: the store's estimate within the
 *  server's deadline (a minute, the browser's start included), and some. */
export const DELIVERY_WAIT_MS = 75_000;
/** A basket changing again within this is asked once, for the last. */
export const DELIVERY_SETTLE_MS = 250;

/** A basket's delivery kept current as it changes, as a sheet uses it: the
 *  purchase's (`createPurchaseDelivery`). */
export type DeliveryQuoteController = {
  snapshot(): DeliveryQuote;
  subscribe(listener: () => void): () => void;
  request(lines: DeliveryLines): void;
  retry(): void;
  invalidate(): void;
  dispose(): void;
};

/** The quote once the store has answered: at once when it has. Every
 *  sheet waits for it this way when Continue is pressed before. */
export const settledQuote = (controller: Pick<DeliveryQuoteController, "snapshot" | "subscribe">) =>
  new Promise<DeliveryQuote>((resolve) => {
    let stop = () => {};
    let done = false;
    const check = () => {
      const quote = controller.snapshot();
      if (done || quote.status === "loading") return;
      done = true;
      stop();
      resolve(quote);
    };
    stop = controller.subscribe(check);
    check();
  });

/** The option a start pins, from an estimate (none for a listing's own
 *  fee, which the server prices itself). */
export const chosenDeliveryOf = (
  quote: DeliveryQuote,
  option: DeliveryOption | null,
  sheetId: string,
): ChosenDelivery | undefined =>
  quote.status === "ready" && quote.estimateId && option
    ? { estimateId: quote.estimateId, sheetId, code: option.code }
    : undefined;

/** Whether the fan chooses among the store's options (the Delivery step):
 *  only when it offers more than one. */
export const deliveryNeedsChoice = (quote: DeliveryQuote) =>
  quote.status === "ready" && quote.options.length > 1;

/** The cheapest option, chosen until the fan chooses another. */
export const cheapestDelivery = (quote: DeliveryQuote): DeliveryOption | null =>
  quote.status === "ready" ? (quote.options[0] ?? null) : null;

const day = (date: string, withMonth: boolean) =>
  new Intl.DateTimeFormat("en-GB", {
    weekday: "short",
    day: "numeric",
    ...(withMonth ? { month: "short" } : {}),
    timeZone: "UTC",
  }).format(new Date(`${date}T12:00:00Z`));

/** When an option arrives, as the store dates it: "Arrives Sun 19 Oct",
 *  "Arrives Mon 20 – Tue 21 Oct"; null when it gives no dates. */
export function deliveryArrives(option: Pick<DeliveryOption, "arrives">): string | null {
  const range = option.arrives;
  if (!range) return null;
  if (range.from === range.to) return `Arrives ${day(range.from, true)}`;
  const sameMonth = range.from.slice(0, 7) === range.to.slice(0, 7);
  return `Arrives ${day(range.from, !sameMonth)} – ${day(range.to, true)}`;
}

/** What the sheet says beside a delivery option that leaves import duties
 *  to the fan (its `dutiesDue`, LIS-1133). */
export const DUTIES_DUE = "Import duties may be due on delivery";

/** What the sheet says beside an option about what is still to pay on
 *  delivery: `DUTIES_DUE`, or nothing. */
export const deliveryDutiesNote = (option: Pick<DeliveryOption, "dutiesDue"> | null | undefined) =>
  option?.dutiesDue ? DUTIES_DUE : null;

/** An option's fee as the sheet says it: "£5.54", or "Free". */
export const deliveryFeeLabel = (option: Pick<DeliveryOption, "feeMinor">, currency: string) =>
  option.feeMinor > 0 ? storeMoneyMinor(option.feeMinor, currency) : "Free";

/** What the sheet says of a basket it cannot quote yet: how many of each
 *  line the store has ("Only 2 left"), that it posts none of it, or that it
 *  did not answer (in its own words, where it gave any). Null otherwise. */
export function deliveryProblem(
  quote: DeliveryQuote,
  nameOf: (itemId: string) => string,
): string | null {
  switch (quote.status) {
    case "short":
      return quote.stock
        .filter((line) => {
          const asked = (JSON.parse(quote.key) as { item_id: string; quantity: number }[])
            .find((candidate) => candidate.item_id === line.item_id)?.quantity ?? 0;
          return line.available < asked;
        })
        .map((line) =>
          line.available > 0
            ? `Only ${line.available} left of ${nameOf(line.item_id)}.`
            : `${nameOf(line.item_id)} has sold out.`,
        )
        .join(" ");
    case "undeliverable":
      return "The store can't post this many. Choose fewer.";
    case "failed":
      return quote.message
        ? `The store couldn't quote delivery: ${quote.message}.`
        : "We couldn't get the store's delivery.";
    default:
      return null;
  }
}
